// Connecting GitHub (Credentials page).
//
// The main way is "Connect with GitHub": Goblic creates its own GitHub App
// through GitHub's pages (manifest flow) and the person installs it on the
// repositories of their choice; nothing is copied by hand. Runs then get a
// one-hour key for just their repository. A personal access token remains as
// the alternative, with a link that opens GitHub's form already filled in.
//
// The round trip through GitHub lands on /connect/github (ConnectGitHubPage).

import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, credentialsApi, githubApi } from '../lib/api-client'
import { useDialogs } from './dialogs'
import { useIllustrations } from '../lib/illustrations'
import { ClayIcon, type ClayIconName } from './goblin-art'
import { Dialog, Field, LoadingRows, Notice, Panel } from './ui'
import { formatRelative } from '../lib/format'

/** Small line icons (16px, 24-unit grid, stroke = currentColor). */
const ICON_PATHS: Record<string, ReactNode> = {
  code: (
    <>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </>
  ),
  pullRequest: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M6 8.5v7" />
      <path d="M13 6h3a2 2 0 0 1 2 2v7.5" />
      <polyline points="15 3.5 12.5 6 15 8.5" />
    </>
  ),
  workflowFile: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="m10 12.5-2 2 2 2" />
      <path d="m14 16.5 2-2-2-2" />
    </>
  ),
  actions: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="m10 8.5 5.5 3.5-5.5 3.5z" />
    </>
  ),
  checks: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>
  ),
  issues: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
}

function PermissionIcon({ name }: { name: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {ICON_PATHS[name]}
    </svg>
  )
}

/** What the App asks for, and why — in the order someone would wonder about them. */
const PERMISSION_REASONS: {
  key: string
  /** Line icon, shown when illustrations are off. */
  icon: string
  /** Clay icon, shown when illustrations are on. */
  art: ClayIconName
  label: string
  why: string
}[] = [
  {
    key: 'contents',
    icon: 'code',
    art: 'code',
    label: 'Code',
    why: 'Clone the repository and push the run’s branch',
  },
  {
    key: 'pull_requests',
    icon: 'pullRequest',
    art: 'pull-request',
    label: 'Pull requests',
    why: 'Open draft pull requests and read review comments',
  },
  {
    key: 'workflows',
    icon: 'workflowFile',
    art: 'workflow',
    label: 'Workflow files',
    why: 'Push changes that touch .github/workflows',
  },
  {
    key: 'actions',
    icon: 'actions',
    art: 'actions',
    label: 'Actions',
    why: 'Read workflow runs and their logs',
  },
  {
    key: 'checks',
    icon: 'checks',
    art: 'checks',
    label: 'Checks and statuses',
    why: 'See whether CI passed',
  },
  {
    key: 'issues',
    icon: 'issues',
    art: 'issues',
    label: 'Issues',
    why: 'Work from GitHub Issues and comment back',
  },
]

/** GitHub's token form, already filled in with the same permissions. */
const TOKEN_TEMPLATE_URL =
  'https://github.com/settings/personal-access-tokens/new?' +
  new URLSearchParams({
    name: 'Goblic',
    description:
      'Used by Goblic runs to push branches, open draft pull requests and read CI results.',
    expires_in: '90',
    contents: 'write',
    pull_requests: 'write',
    workflows: 'write',
    actions: 'read',
    statuses: 'read',
    issues: 'write',
    metadata: 'read',
  }).toString()

function AccessBadge({ level }: { level: string | undefined }) {
  return level === 'write' ? (
    <span className="shrink-0 rounded-full bg-info-soft px-2 py-0.5 text-xs font-medium text-info">
      Read &amp; write
    </span>
  ) : (
    <span className="shrink-0 rounded-full border border-line px-2 py-0.5 text-xs font-medium text-ink-3">
      Read only
    </span>
  )
}

export function GitHubPanel({ workspaceId }: { workspaceId: string }) {
  const [dialog, setDialog] = useState<'connect' | 'token' | null>(null)
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const statusQuery = useQuery({
    queryKey: ['github', workspaceId],
    queryFn: () => githubApi.status(workspaceId),
    enabled: workspaceId !== '',
  })
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['github', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['credentials', workspaceId] }),
    ])

  const disconnect = useMutation({
    mutationFn: async (target: {
      credentialId: string
      kind: 'app' | 'token'
    }) => {
      if (target.kind === 'app')
        await githubApi.disconnectApp(workspaceId, target.credentialId)
      else await credentialsApi.revoke(target.credentialId, workspaceId)
    },
    onSuccess: refresh,
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t disconnect GitHub',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  const status = statusQuery.data
  const apps = status?.apps ?? []
  const installed = apps.some((app) => app.installations.length > 0)

  async function handleDisconnect(
    target: { credentialId: string; kind: 'app' | 'token' },
    label: string
  ): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Disconnect ${label}?`,
      message:
        target.kind === 'app'
          ? 'Goblic removes this App from its repositories. Runs on those repositories keep their changes as a diff until you connect again. You can delete the App itself on GitHub afterwards.'
          : 'Runs keep their changes as a diff but won’t push or open pull requests until you connect again.',
      confirmLabel: 'Disconnect',
    })
    if (ok) disconnect.mutate(target)
  }

  let description: string
  if (installed)
    description =
      apps.length > 1
        ? `Connected to ${apps.length} accounts or organizations. Runs push their branch and open a draft pull request.`
        : 'Connected. Runs push their branch and open a draft pull request.'
  else if (apps.length > 0)
    description =
      'Almost there: install the App on the repositories Goblic may work in.'
  else if (status?.token)
    description = `Connected with a personal access token ${formatRelative(status.token.connectedAt)}.`
  else
    description =
      'Not connected. Runs keep their changes as a diff; nothing is pushed.'

  return (
    <Panel
      className="mb-6"
      title="GitHub"
      description={description}
      actions={
        status ? (
          <>
            {status.token ? (
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() =>
                  void handleDisconnect(
                    { credentialId: status.token!.credentialId, kind: 'token' },
                    'the token'
                  )
                }
                disabled={disconnect.isPending}
              >
                Disconnect token
              </button>
            ) : null}
            <button
              type="button"
              className={
                apps.length > 0 ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'
              }
              onClick={() => setDialog('connect')}
            >
              {apps.length > 0
                ? 'Connect another account'
                : 'Connect with GitHub'}
            </button>
          </>
        ) : null
      }
      flush={apps.length > 0}
    >
      {statusQuery.isLoading ? (
        <LoadingRows />
      ) : apps.length > 0 ? (
        <ul className="divide-y divide-line">
          {apps.map((app) => (
            <li key={app.credentialId} className="px-5 py-3">
              <div className="flex items-center justify-between gap-4">
                <span className="min-w-0">
                  <span className="block text-base font-medium text-ink">
                    {app.owner || app.slug}
                  </span>
                  <span className="block text-sm text-ink-3">
                    App {app.slug}
                    {app.installations.length === 0
                      ? ' · not installed yet'
                      : ''}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <a
                    href={app.installUrl}
                    className={
                      app.installations.length > 0
                        ? 'btn-secondary btn-sm'
                        : 'btn-primary btn-sm'
                    }
                  >
                    {app.installations.length > 0
                      ? 'Add repositories'
                      : 'Install on repositories'}
                  </a>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() =>
                      void handleDisconnect(
                        { credentialId: app.credentialId, kind: 'app' },
                        app.owner || app.slug
                      )
                    }
                    disabled={disconnect.isPending}
                  >
                    Disconnect
                  </button>
                </span>
              </div>
              {app.installations.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {app.installations.map((installation) => (
                    <li
                      key={installation.id}
                      className="flex items-center justify-between gap-4 text-sm text-ink-3"
                    >
                      <span>
                        {installation.account} ·{' '}
                        {installation.repositorySelection === 'all'
                          ? 'all repositories'
                          : 'selected repositories'}
                      </span>
                      <a
                        href={installation.htmlUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="link"
                      >
                        Change on GitHub
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : status?.token ? (
        <p className="text-base text-ink-2">
          Works, but the token is tied to your account and sees everything it
          was given. With <em>Connect with GitHub</em> each run gets a key for
          just its own repository, valid for an hour.
        </p>
      ) : (
        <p className="text-base text-ink-2">
          Connect once and Goblic can push branches, open draft pull requests
          and read CI results on the repositories you choose. Work in several
          organizations by connecting each one.
        </p>
      )}

      <Dialog
        open={dialog === 'connect'}
        onClose={() => setDialog(null)}
        title="Connect GitHub"
        description="Goblic creates its own GitHub App on your account or organization. You choose the repositories; each run gets a key for just its own repository, valid for an hour."
        art="/goblins/dialog-connect.webp"
        size="md"
      >
        <ConnectForm
          workspaceId={workspaceId}
          permissions={status?.permissions ?? {}}
          onUseToken={() => setDialog('token')}
        />
      </Dialog>
      <Dialog
        open={dialog === 'token'}
        onClose={() => setDialog(null)}
        title={
          status?.token ? 'Replace GitHub token' : 'Use a personal access token'
        }
        description="GitHub’s form opens with the right permissions already filled in. Choose the repositories, create the token and paste it here."
        art="/goblins/dialog-connect.webp"
        size="md"
      >
        <GitHubTokenForm
          workspaceId={workspaceId}
          onDone={() => {
            setDialog(null)
            void refresh()
          }}
        />
      </Dialog>
    </Panel>
  )
}

export function ConnectForm({
  workspaceId,
  permissions,
  onUseToken,
  onBeforeLeave,
}: {
  workspaceId: string
  permissions: Record<string, string>
  onUseToken: () => void
  /** Called just before the browser leaves for GitHub (e.g. to remember where to come back to). */
  onBeforeLeave?: () => void
}) {
  const [illustrations] = useIllustrations()
  const [owner, setOwner] = useState<'me' | 'org'>('me')
  const [organization, setOrganization] = useState('')
  const start = useMutation({
    mutationFn: () =>
      githubApi.startManifest({
        workspaceId,
        webOrigin: window.location.origin,
        organization: owner === 'org' ? organization.trim() : undefined,
      }),
    // GitHub takes the manifest as a form POST, so build one and submit it:
    // the browser leaves for GitHub's "Create GitHub App" page.
    onSuccess: ({ postUrl, manifest }) => {
      onBeforeLeave?.()
      const form = document.createElement('form')
      form.method = 'post'
      form.action = postUrl
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'manifest'
      input.value = manifest
      form.appendChild(input)
      document.body.appendChild(form)
      form.submit()
    },
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    start.mutate()
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <p className="mb-2 text-base font-medium text-ink">What it may do</p>
        <ul className="divide-y divide-line rounded-control border border-line">
          {PERMISSION_REASONS.map((p) => (
            <li key={p.key} className="flex items-center gap-3 px-3 py-2">
              {illustrations ? (
                <ClayIcon name={p.art} size={32} />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-moss-soft text-moss">
                  <PermissionIcon name={p.icon} />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-base font-medium leading-tight text-ink">
                  {p.label}
                </span>
                <span className="block text-sm text-ink-3">{p.why}</span>
              </span>
              <AccessBadge level={permissions[p.key]} />
            </li>
          ))}
        </ul>
        <p className="mt-2 text-sm text-ink-3">
          Each run only gets code, pull requests and workflow files, for its own
          repository.
        </p>
      </div>

      <fieldset>
        <legend className="mb-1.5 text-base font-medium text-ink">
          Create it under
        </legend>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-ink">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="github-owner"
              checked={owner === 'me'}
              onChange={() => setOwner('me')}
            />
            My account
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="github-owner"
              checked={owner === 'org'}
              onChange={() => setOwner('org')}
            />
            An organization
          </label>
        </div>
        {owner === 'org' ? (
          <input
            className="field-input mt-2"
            placeholder="Organization name, e.g. acme"
            aria-label="Organization name"
            value={organization}
            onChange={(event) => setOrganization(event.target.value)}
            autoCapitalize="none"
            spellCheck={false}
            required
            autoFocus
          />
        ) : null}
      </fieldset>

      {start.isError ? (
        <Notice tone="error">
          {start.error instanceof ApiError
            ? start.error.message
            : 'Couldn’t start connecting.'}
        </Notice>
      ) : null}

      <div className="flex items-center justify-between gap-3 border-t border-line pt-4">
        <button type="button" className="btn-ghost btn-sm" onClick={onUseToken}>
          Use a personal access token instead
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={
            start.isPending || (owner === 'org' && !organization.trim())
          }
        >
          {start.isPending ? 'Opening GitHub…' : 'Continue on GitHub'}
        </button>
      </div>
    </form>
  )
}

export function GitHubTokenForm({
  workspaceId,
  onDone,
}: {
  workspaceId: string
  onDone: () => void
}) {
  const [token, setToken] = useState('')
  const save = useMutation({
    mutationFn: () =>
      credentialsApi.connectGitHub({ workspaceId, token: token.trim() }),
    onSuccess: onDone,
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    save.mutate()
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <a
        href={TOKEN_TEMPLATE_URL}
        target="_blank"
        rel="noreferrer"
        className="btn-secondary w-full justify-center"
      >
        Create a token on GitHub
      </a>
      <Field label="Paste the token" htmlFor="github-token">
        <input
          id="github-token"
          type="password"
          className="field-input font-mono"
          placeholder="github_pat_…"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
        />
      </Field>
      {save.isError ? (
        <Notice tone="error">
          {save.error instanceof ApiError
            ? save.error.message
            : 'Couldn’t save the token.'}
        </Notice>
      ) : null}
      <div className="flex justify-end border-t border-line pt-4">
        <button
          type="submit"
          className="btn-primary"
          disabled={save.isPending || token.trim().length < 20}
        >
          {save.isPending ? 'Saving…' : 'Save token'}
        </button>
      </div>
    </form>
  )
}
